'use client'

import { Children, type ReactElement, useCallback, useContext, useEffect, useState } from 'react'

import cn from 'clsx'
import { HelpCenterContext, type labelDefaults } from '@/components/HtmlArticle/HelpCenterContext'

import css from './Platforms.module.css'

import { Box, type BoxProps } from '@/components/Box/Box'
import { SelectField } from '@/components/future/select-field/select-field'
import { Text } from '@/components/Text/Text'

type PlatformName = keyof (typeof labelDefaults)['platforms']

interface PlatformsProps extends BoxProps {
    selectedPlatform?: string
    onSelectPlatform: (platform: string) => void
}

export function Platforms({
    children,
    className,
    selectedPlatform,
    onSelectPlatform,
    ...rest
}: PlatformsProps) {
    const { labels } = useContext(HelpCenterContext)
    const [availablePlatforms] = useState(() => {
        const result: string[] = []

        for (const child of Children.toArray(children)) {
            if (typeof child === 'object' && 'props' in child && child.props['data-platform']) {
                result.push(child.props['data-platform'])
            }
        }

        return result
    })

    const getValidPlatform = useCallback(
        (suggestedPlatform = '') => {
            const [firstPlatform] = availablePlatforms
            return availablePlatforms.includes(suggestedPlatform)
                ? suggestedPlatform
                : firstPlatform
        },
        [availablePlatforms],
    )

    const [currentPlatform, setCurrentPlatform] = useState(getValidPlatform(selectedPlatform))

    useEffect(() => {
        setCurrentPlatform(getValidPlatform(selectedPlatform))
    }, [selectedPlatform, getValidPlatform])

    const platformComponents: Record<string, ReactElement> = {}
    for (const child of Children.toArray(children)) {
        if (typeof child === 'object' && 'props' in child && child.props) {
            const platformName = child.props['data-platform']
            if (typeof platformName === 'string') {
                platformComponents[platformName] = child
            }
        }
    }

    return (
        <Box className={cn(css.platforms, className)} {...rest}>
            <Box className={css.selector}>
                <Text size="minor-base" weight="semibold" tag="label" htmlFor="platform-select">
                    {labels.platforms.select}
                </Text>
                <SelectField
                    id="platform-select"
                    size="sm"
                    aria-label={labels.platforms.select}
                    value={currentPlatform}
                    onChange={(e) => {
                        if (onSelectPlatform) {
                            onSelectPlatform(e.currentTarget.value)
                        }
                    }}
                >
                    {availablePlatforms.map((p) => (
                        <option key={p} defaultValue={p} value={p}>
                            {p
                                .split(' ')
                                .map((name) => labels.platforms[name as PlatformName])
                                .join(', ')}
                        </option>
                    ))}
                </SelectField>
            </Box>
            <Box key={`current-platform-${currentPlatform}`} className={css.content}>
                {platformComponents[currentPlatform]}
            </Box>
        </Box>
    )
}
